import {
  FinanceProvider,
  FinanceRate,
  InfoWorkType,
  Job,
  WorkType,
} from '@kanda-libs/ks-frontend-services';

import { pipe } from 'fp-ts/lib/function';

export type FinanceRateCosts = {
  jobTotal: number;
  deposit: number;
  loan: number;
  monthlyCost: number;
  interestFee: number;
  total: number;
};

export type GroupedByKey = Record<string, FinanceRate[]>;

export type GroupedByAprAndDuration = Record<string, GroupedByKey>;

export const FINANCE_PROVIDER_PRIORITY_ORDER: FinanceProvider[] = [
  'allium',
  'propensio',
  'humm',
  'etika',
  'omni',
  'novuna',
  'demo',
];

export const filterForUnregulatedRates = (
  rates: FinanceRate[],
): FinanceRate[] =>
  rates.filter(
    (rate: FinanceRate) =>
      rate.apr_type === 'INTEREST_FREE' && (rate.duration || 12) < 12,
  );

export const filterForRegulatedRates = (rates: FinanceRate[]): FinanceRate[] =>
  rates.filter(
    (rate: FinanceRate) =>
      rate.apr_type !== 'INTEREST_FREE' || (rate.duration || 0) >= 12,
  );

export const filterForSecondLineRates = (rates: FinanceRate[]): FinanceRate[] =>
  rates.filter((rate: FinanceRate) => rate.finance_types.includes('secondary'));

export const filterForEnabledRates = (rates: FinanceRate[]): FinanceRate[] =>
  rates.filter((rate) => rate.enabled);

export const filterInfoWorkType = (
  workType: WorkType,
  infoWorkType: InfoWorkType[],
): InfoWorkType | undefined =>
  infoWorkType.filter((info: InfoWorkType) => info.work_type === workType)?.[0];

export const checkHasSecondLine = (job?: Job): boolean => {
  if (!job) return false;
  if (!job.finance_options) return false;
  if (job.finance_status === 'not_applied') return false;
  const rates = filterForSecondLineRates(job.finance_options);
  return rates.length > 0;
};

export const matchRates = (
  userRates: FinanceRate[],
  availableRates?: FinanceRate[],
): FinanceRate[] => {
  if (!availableRates) return userRates;
  return availableRates.reduce(
    (final: FinanceRate[], currentAvailable: FinanceRate) => {
      const matched = userRates.filter(
        (rate) =>
          rate.name === currentAvailable.name &&
          rate.provider === currentAvailable.provider &&
          rate.finance_types.every((type) =>
            currentAvailable.finance_types.includes(type),
          ),
      );
      if (matched.length !== 1) return final;
      return [...final, matched[0]];
    },
    [],
  );
};

export const getCurrentInfoWorkType = (
  job: Job | undefined,
  infoWorkType: InfoWorkType[] | undefined,
): InfoWorkType | undefined => {
  const workType = job?.work_type;
  const rates = job?.finance_options || [];
  const isSecondLine = checkHasSecondLine(job);
  if (!rates || !infoWorkType || !workType) return undefined;
  const filteredWorkType = filterInfoWorkType(workType, infoWorkType);
  if (!filteredWorkType) return undefined;
  // const finalRates =
  //   rates.length === 0
  //     ? filterForRegulatedRates(filteredWorkType.rates)
  //     : rates;
  if (isSecondLine) {
    const applicableRates = pipe(rates, filterForSecondLineRates);
    return {
      ...filteredWorkType,
      rates: applicableRates,
    };
  }
  const applicableRates = pipe(rates, filterForEnabledRates);
  const matchedRates = matchRates(applicableRates, filteredWorkType.rates);
  return {
    ...filteredWorkType,
    rates: matchedRates,
  };
};

export const checkRatePossibleValid = (
  rate: FinanceRate,
  total: number,
  setMinimumDeposit: number,
): boolean => {
  const {
    min_deposit_pct: minDepPct = 0,
    max_deposit_pct: maxDepPct = 0,
    min_deposit_value: minDepVal = 0,
    max_deposit_value: maxDepVal = 0,
    min_job_value: minJobVal = 0,
    max_job_value: maxJobVal = 0,
  } = rate;

  // Get set minimum deposit as %
  const setMinimumDepositPct = 10000 * (setMinimumDeposit / total);

  const maxPossibleDeposit = Math.min(
    total * (maxDepPct / 10000),
    maxDepVal || Infinity,
  );
  const minPossibleDeposit = Math.max(
    setMinimumDeposit,
    total * (minDepPct / 10000),
    minDepVal,
  );

  const minPossibleLoan = total - maxPossibleDeposit;
  const maxPossibleLoan = total - minPossibleDeposit;

  // Minimum loan more than maximum value
  if (minPossibleLoan > maxJobVal) return false;
  // Maximum loan more than minimum value
  if (maxPossibleLoan < minJobVal) return false;
  // Set minimum deposit more than minimum deposit percentage
  if (setMinimumDepositPct > maxDepPct) return false;

  return maxPossibleLoan >= minJobVal || minPossibleLoan <= maxJobVal;
};

export const getFinanceRateFullInfoKey = (rate: FinanceRate): string =>
  [
    rate.provider,
    rate.apr,
    rate.duration,
    rate.fee,
    rate.charge,
    rate?.deferred_duration ? 'bnpl' : undefined,
  ]
    .filter((val) => val !== undefined)
    .join('-');

export type FilterProviderType = 'include' | 'exclude';

export const filterRatesByProvider = (
  rates: FinanceRate[],
  provider: FinanceProvider,
  type: FilterProviderType = 'include',
): FinanceRate[] =>
  rates.filter((rate) =>
    type === 'include'
      ? rate.provider === provider
      : rate.provider !== provider,
  );

export const hasMatchingRateInArray = (
  rates: FinanceRate[],
  rate: FinanceRate,
): boolean =>
  rates.some(
    (current) =>
      getFinanceRateFullInfoKey(current) === getFinanceRateFullInfoKey(rate),
  );

export const filterForInterestBearingRates = (
  rates: FinanceRate[],
): FinanceRate[] => rates.filter((rate) => rate.apr_type !== 'INTEREST_FREE');

export const filterForInterestFreeRates = (
  rates: FinanceRate[],
): FinanceRate[] => rates.filter((rate) => rate.apr_type === 'INTEREST_FREE');

export const removeHummIBIfOtherIB = (
  rates: FinanceRate[],
  total: number,
  deposit: number,
): FinanceRate[] => {
  // Get all IB rates
  const ibRates = filterForInterestBearingRates(rates);
  // Get non-humm IB rates
  const nonHummIBRates = filterRatesByProvider(ibRates, 'humm', 'exclude');
  // Check non-humm IB rates for valid rates
  const validNonHummIBRates = nonHummIBRates.filter((rate) =>
    checkRatePossibleValid(rate, total, deposit),
  );
  // If there are some valid rates, return non-humm rates
  if (validNonHummIBRates.length > 0) return nonHummIBRates;
  // Else, return all rates
  return ibRates;
};

export const filterOutHummRegulatedIfOtherRegulated = (
  rates: FinanceRate[],
  total: number,
  deposit: number,
): FinanceRate[] => {
  const validRates = rates.filter((rate) =>
    checkRatePossibleValid(rate, total, deposit),
  );
  const unregulated = filterForUnregulatedRates(validRates);
  const regulated = filterForRegulatedRates(validRates);
  const ibRates = removeHummIBIfOtherIB(regulated, total, deposit);
  const ifRates = filterForInterestFreeRates(rates);
  // const validRegulatedRates = regulatedRates.filter((rate) =>
  //   checkRatePossibleValid(rate, total, deposit),
  // );
  // const hummRates = filterRatesByProvider(validRegulatedRates, 'humm');
  // // .filter(
  // //   (rate: FinanceRate) => rate.apr_type !== 'INTEREST_FREE',
  // // );
  // const validHummRates = hummRates.filter((rate) =>
  //   checkRatePossibleValid(rate, total, deposit),
  // );
  // if (validHummRates.length === validRegulatedRates.length) return rates;
  return [
    ...unregulated,
    ...ifRates,
    ...ibRates,
    // ...regulatedRates.filter(
    //   (rate) => !hasMatchingRateInArray(hummRates, rate),
    // ),
  ];
};

export const filterOut149If129 = (
  rates: FinanceRate[],
  total: number,
  deposit: number,
): FinanceRate[] => {
  const rates129 = rates.filter((rate) => rate.apr === 1290);
  if (rates129.length === 0) return rates;
  const validRates129 = rates129.filter((rate) =>
    checkRatePossibleValid(rate, total, deposit),
  );
  if (validRates129.length === 0) return rates;
  return rates.filter(
    (rate) =>
      !((rate.apr || 0) === 1490 && rate.apr_type === 'INTEREST_BEARING'),
  );
};

export const filterOutPropensio149IfAllium149 = (
  rates: FinanceRate[],
  total: number,
  deposit: number,
): FinanceRate[] => {
  const allium149 = rates
    .filter((rate) => rate.provider === 'allium' && rate.apr === 1490)
    .filter((rate) => checkRatePossibleValid(rate, total, deposit));
  if (allium149.length === 0) return rates;
  return rates.filter((rate) => {
    if (rate.provider !== 'propensio') return true;
    if (rate.apr_type !== 'INTEREST_BEARING') return true;
    return rate.apr !== 1490;
  });
};

export const filterRateIsPossibleValid = (
  rates: FinanceRate[],
  total: number,
  minimumDeposit: number,
): FinanceRate[] =>
  rates.filter((rate) => checkRatePossibleValid(rate, total, minimumDeposit));

export const filterPossibleValidRates = (
  infoWorkType: InfoWorkType,
  total: number,
  minimumDeposit: number,
): InfoWorkType => {
  const { rates: infoRates, ...rest } = infoWorkType;

  // TODO: FIX THESE!
  // - look at:
  //   - filterOut149If129
  //   - filterOutPropensio149IfAllium149
  //   - filterOutHummRegulatedIfOtherRegulated
  // to include total and deposit
  const rates = pipe(
    infoRates,
    // (r1) => filterOutHummRegulatedIfOtherRegulated(r1, total, minimumDeposit),
    (r2) => filterRateIsPossibleValid(r2, total, minimumDeposit),
    (r3) => filterOut149If129(r3, total, minimumDeposit),
    (r4) => filterOutPropensio149IfAllium149(r4, total, minimumDeposit),
  );

  return {
    ...rest,
    rates,
  };
};

export const getAprKey = (rate: FinanceRate): string => {
  const { apr = 0, apr_type: aprType = 'INTEREST_FREE' } = rate;
  return `${apr}-${aprType}`;
};

export const getAprFromKey = (key: string) => {
  const parts = key.split('-');
  return parseInt(parts[0], 10);
};

export const getAprTypeFromKey = (key: string) => {
  const parts = key.split('-');
  return parts[1];
};

export const groupByApr = (rates: FinanceRate[]): GroupedByKey =>
  rates.reduce((final: GroupedByKey, current: FinanceRate) => {
    const key = getAprKey(current);
    if (Object.keys(final).includes(key))
      return {
        ...final,
        [key]: [...final[key], current],
      };
    return {
      ...final,
      [key]: [current],
    };
  }, {});

export const getDurationKey = (rate: FinanceRate): string => {
  const {
    duration = 0,
    deferred_duration = 0,
    apr_type: aprType = 'INTEREST_FREE',
  } = rate;
  if (aprType === 'BUYNOW_PAYLATER') return `${deferred_duration}-${duration}`;
  return `${duration}`;
};

export const getDurationFromKey = (key: string): number => {
  if (!key.includes('-')) return parseInt(key, 10);
  const parts = key.split('-');
  return parseInt(parts[1], 10);
};

export const getBNPLDurationFromKey = (key: string): number[] => {
  const parts = key.split('-');
  return [parseInt(parts[0], 10), parseInt(parts[1], 10)];
};

export const groupByDuration = (rates: FinanceRate[]): GroupedByKey =>
  rates.reduce((final: GroupedByKey, current: FinanceRate) => {
    const key = getDurationKey(current);
    if (Object.keys(final).includes(key))
      return {
        ...final,
        [key]: [...final[key], current],
      };
    return {
      ...final,
      [key]: [current],
    };
  }, {});

export const groupGroupedByAprByDuration = (
  grouped: GroupedByKey,
): GroupedByAprAndDuration =>
  Object.keys(grouped).reduce((final: GroupedByAprAndDuration, apr: string) => {
    const groupedByDuration = groupByDuration(grouped[apr]);
    return {
      ...final,
      [apr]: groupedByDuration,
    };
  }, {});

export const sortGroupedByLenderPriority = (
  grouped: GroupedByAprAndDuration,
  primary?: FinanceProvider,
): GroupedByAprAndDuration =>
  Object.keys(grouped).reduce((final: GroupedByAprAndDuration, apr: string) => {
    const sorted = Object.keys(grouped[apr]).reduce(
      (intermediate: GroupedByKey, duration: string) => {
        const rates = grouped[apr][duration].sort(
          (r1: FinanceRate, r2: FinanceRate) => {
            if (primary && r1.provider === primary) return -1;
            if (primary && r2.provider === primary) return 1;
            const idx1 =
              FINANCE_PROVIDER_PRIORITY_ORDER.indexOf(r1.provider) || Infinity;
            const idx2 =
              FINANCE_PROVIDER_PRIORITY_ORDER.indexOf(r2.provider) || Infinity;
            return idx1 - idx2;
          },
        );
        return {
          ...intermediate,
          [duration]: rates,
        };
      },
      {},
    );
    return {
      ...final,
      [apr]: sorted,
    };
  }, {});

export const groupByAprAndDuration = (
  infoWorkType: InfoWorkType,
): GroupedByAprAndDuration =>
  pipe(infoWorkType.rates, groupByApr, groupGroupedByAprByDuration, (grouped) =>
    sortGroupedByLenderPriority(grouped, infoWorkType.primary),
  );

// DEV_NOTE: Better logic could be used here to determine an
// actual infoWorkType for enterprise/have BE updated to
// handle eneterprise infoWorkType
export const convertIntroRatesToWorkType = (job: Job): InfoWorkType => {
  const rates = job?.finance_options || [];
  const workType = job?.work_type || 'kitchens_supply_only';
  const product = 'Intro';
  if (rates.length === 0)
    return {
      rates,
      work_type: workType,
      product,
      primary: 'propensio',
    };
  const counts = rates
    .map((rate) => rate.provider)
    .reduce((final: Record<string, number>, current: FinanceProvider) => {
      if (Object.keys(final).includes(current)) {
        return {
          ...final,
          [current]: final[current] + 1,
        };
      }
      return {
        ...final,
        [current]: 1,
      };
    }, {});
  const max = Math.max(...Object.values(counts));
  const primary = Object.keys(counts).find(
    (key) => counts[key] === max,
  ) as FinanceProvider;
  return {
    rates,
    work_type: workType,
    product,
    primary,
  };
};

export const convertExampleRatesToWorkType = (job: Job): InfoWorkType => {
  const rates = job?.finance_options || [];
  return {
    work_type: 'other',
    rates,
    product: 'other',
    primary: 'propensio',
  };
};

export const getValidFinanceRates = (
  job: Job | undefined,
  infoWorkType: InfoWorkType | undefined,
  isExampleJob: boolean,
): GroupedByAprAndDuration | undefined => {
  if (!job) return undefined;
  const total = job?.total?.amount_vat_inclusive?.amount || 0;
  const minimumDeposit = job?.deposit_value?.amount || 0;
  const isIntroduced = Boolean(job?.introduced_to);
  if (isIntroduced)
    return pipe(
      job,
      convertIntroRatesToWorkType,
      (info) => filterPossibleValidRates(info, total, minimumDeposit),
      groupByAprAndDuration,
    );
  if (isExampleJob)
    return pipe(
      job,
      convertExampleRatesToWorkType,
      (info) => filterPossibleValidRates(info, total, minimumDeposit),
      groupByAprAndDuration,
    );
  if (!infoWorkType) return undefined;
  return pipe(
    infoWorkType,
    (info) => filterPossibleValidRates(info, total, minimumDeposit),
    groupByAprAndDuration,
  );
};

export const getAprPercentageLabel = (apr: number) => {
  const pct = apr / 100;
  return `${pct}% APR`;
};

export const getDurationLabel = (term: number, forceMonths = false): string => {
  if (forceMonths) return `${term} months`;
  if (term === 1) return '1 month';
  if (term === 12) return '1 year';
  if (term < 12 || term % 12 !== 0) return `${term} months`;
  return `${term / 12} years`;
};

export const getBNPLDurationLabel = (term: string): string =>
  `${term.replace('-', ' + ')} months`;

export const getMaximumBorrowAmount = (rates: FinanceRate[]): number => {
  const hasCreditLine = rates.some((rate) => Boolean(rate.credit_line));
  if (hasCreditLine)
    return (
      rates
        .map((rate) => rate.credit_line)
        .filter((line) => line !== undefined)
        .sort((l1, l2) => (l2 || 0) - (l1 || 0))?.[0] || 0
    );
  return (
    rates
      .map((rate) => rate.max_job_value)
      .filter((line) => line !== undefined)
      .sort((l1, l2) => (l2 || 0) - (l1 || 0))?.[0] || 0
  );
};

export const checkWithinValueBounds = (
  loan: number,
  min: number,
  max: number,
) => {
  if (loan < min) return false;
  if (max === 0) return true;
  return loan <= max;
};

export const checkWithinDepositPercentageBounds = (
  depositPct: number,
  minPct: number,
  maxPct: number,
) => {
  if (depositPct < minPct) return false;
  if (maxPct === 0) return true;
  return depositPct <= maxPct;
};

export const checkWithinDeposiValueBounds = (
  deposit: number,
  min: number,
  max: number,
) => {
  if (deposit < min) return false;
  if (max === 0) return true;
  return deposit <= max;
};

export const checkRateIsValid = (
  rate: FinanceRate,
  total: number,
  deposit: number,
): boolean => {
  const loan = total - deposit;
  const depositPct = (10000 * deposit) / total;

  const {
    min_deposit_pct: minDepPct = 0,
    max_deposit_pct: maxDepPct = 0,
    min_deposit_value: minDepVal = 0,
    max_deposit_value: maxDepVal = 0,
    min_job_value: minJobVal = 0,
    max_job_value: maxJobVal = 0,
  } = rate;

  const withinValue = checkWithinValueBounds(loan, minJobVal, maxJobVal);
  const withinDepPct = checkWithinDepositPercentageBounds(
    depositPct,
    minDepPct,
    maxDepPct,
  );
  const withinDepVal = checkWithinDeposiValueBounds(
    deposit,
    minDepVal,
    maxDepVal,
  );

  return withinValue && withinDepPct && withinDepVal;
};

export interface FinanceRateDepositBounds {
  min: number;
  max: number;
}

export const getRateDepositBounds = (
  rate: FinanceRate,
  total: number,
  minimumDeposit = 0,
): FinanceRateDepositBounds => {
  const {
    min_deposit_pct: minDepPct = 0,
    max_deposit_pct: maxDepPct = 0,
    min_deposit_value: minDepVal = 0,
    max_deposit_value: maxDepVal = 0,
    min_job_value: minJobVal = 0,
    max_job_value: maxJobVal = 0,
  } = rate;

  const minValues = [
    minimumDeposit,
    minDepVal,
    (total * minDepPct) / 10000,
    maxJobVal ? total - maxJobVal : 0,
  ];
  const min = Math.max(...minValues);

  const maxValues = [
    total - minimumDeposit,
    maxDepVal || Infinity,
    maxDepPct ? (total * maxDepPct) / 10000 : Infinity,
    minJobVal ? total - minJobVal : Infinity,
  ];
  const max = Math.min(...maxValues);

  return {
    min,
    max,
  };
};

export const getRatesDepositBounds = (
  rates: FinanceRate[],
  total: number,
  minimumDeposit = 0,
): FinanceRateDepositBounds => {
  if (rates.length === 1)
    return getRateDepositBounds(rates[0], total, minimumDeposit);
  const rateBounds = rates
    .map((rate) => getRateDepositBounds(rate, total, minimumDeposit))
    .reduce(
      (final: FinanceRateDepositBounds, current: FinanceRateDepositBounds) => ({
        min: Math.min(final.min, current.min),
        max: Math.max(final.max, current.max),
      }),
      {
        min: 0,
        max: 0,
      },
    );
  return {
    min: Math.max(rateBounds.min, minimumDeposit),
    max: Math.min(rateBounds.max, total - minimumDeposit),
  };
};

export const prioritiseAlliumRates = (
  rates: FinanceRate[],
  total: number,
  minimumDeposit = 0,
): FinanceRate[] => {
  const hasAllium = rates.some((rate) => rate.provider === 'allium');
  if (!hasAllium) return rates;
  const validAlliumRates = rates
    .filter((rate) => rate.provider === 'allium')
    .filter((rate) => checkRateIsValid(rate, total, minimumDeposit));
  if (validAlliumRates.length === 0) return rates;
  return validAlliumRates;
};

export const getMostAppropriateRate = (
  rates: FinanceRate[],
  total: number,
  deposit: number,
): FinanceRate | undefined => {
  const filtered = rates.filter((rate) =>
    checkRateIsValid(rate, total, deposit),
  );
  return filtered?.[0];
};

export const calculateMonthlyRepaymentRaw = (
  loan: number,
  apr: number,
  terms: number,
): number => {
  if (apr === 0) return loan / terms;
  const rate = apr / 12 / 10000;
  const compoundInterest = (1 + rate) ** terms;
  return (loan * (rate * compoundInterest)) / (compoundInterest - 1);
};

export const calculateFinanceRateCosts = (
  rate: FinanceRate,
  jobTotal: number,
  deposit: number,
): FinanceRateCosts => {
  const loan = jobTotal - deposit;

  const { apr = 0, duration = 1 } = rate;

  const monthly = calculateMonthlyRepaymentRaw(loan, apr, duration);

  const monthlyCost = Math.round(monthly);

  const total = Math.round(monthly * duration);

  const interestFee = total - loan;

  return {
    jobTotal,
    deposit,
    loan,
    monthlyCost,
    interestFee,
    total,
  };
};

export const getFinanceRateMaximumAllowedDepositValue = (
  total: number,
  rate: FinanceRate,
): number => {
  // Get maximum value - fallback to Inf
  const maxDepositValue = rate.max_deposit_value ?? Infinity;
  // Get maximum % - fallback to 50%
  const maxDepositPercentage = rate.max_deposit_pct ?? 5000;
  // Calculate max deposit % value
  const maxDepositPercentageValue = Math.floor(
    (maxDepositPercentage * total) / 10000,
  );
  return Math.min(maxDepositValue, maxDepositPercentageValue);
};

export const getTotalLoanCostForRate = (
  rate: FinanceRate,
  total: number,
  deposit: number,
  canChangeDeposit = false,
): number => {
  const { apr = 0, duration = 1 } = rate;
  if (!canChangeDeposit) {
    const loan = total - deposit;
    const monthly = calculateMonthlyRepaymentRaw(loan, apr, duration);
    return Math.round(monthly * duration);
  }
  const maximumAllowedDeposit = getFinanceRateMaximumAllowedDepositValue(
    total,
    rate,
  );
  const loan = total - maximumAllowedDeposit;
  const monthly = calculateMonthlyRepaymentRaw(loan, apr, duration);
  return Math.round(monthly * duration);
};

export const getMinimumMonthlyForRate = (
  rate: FinanceRate,
  total: number,
  deposit: number,
  canChangeDeposit = false,
): number => {
  const { apr = 0, duration = 1 } = rate;
  if (!canChangeDeposit) {
    const loan = total - deposit;
    const monthly = calculateMonthlyRepaymentRaw(loan, apr, duration);
    return Math.round(monthly);
  }
  const maximumAllowedDeposit = getFinanceRateMaximumAllowedDepositValue(
    total,
    rate,
  );
  const loan = total - maximumAllowedDeposit;
  const monthly = calculateMonthlyRepaymentRaw(loan, apr, duration);
  return Math.round(monthly);
};

export const getRateWithMinimumMonthly = (
  job: Job | undefined,
  allRates: GroupedByAprAndDuration | undefined,
): FinanceRate | undefined => {
  if (!job || !allRates || Object.keys(allRates).length === 0) return undefined;
  const total = job.total?.amount_vat_inclusive?.amount ?? 0;
  const deposit = job.deposit_value.amount;
  const depositType = job.deposit_type;
  const rates = Object.keys(allRates).reduce(
    (ratesArray: FinanceRate[], groupedKey) => {
      const aprDurations = allRates[groupedKey];
      const aprRates = Object.keys(aprDurations).reduce(
        (aprRatesArray: FinanceRate[], key) => {
          const durationRates = aprDurations[key];
          return [...aprRatesArray, ...durationRates];
        },
        [],
      );
      return [...ratesArray, ...aprRates];
    },
    [],
  );
  const monthlyAndRate = rates.map((rate) => {
    const monthy = getMinimumMonthlyForRate(
      rate,
      total,
      deposit,
      depositType === 'no_deposit',
    );
    return {
      rate,
      monthy,
    };
  });
  return monthlyAndRate.sort((m1, m2) => m1.monthy - m2.monthy)[0].rate;
};

export const getMinimumMonthlyForRates = (
  rates: FinanceRate[],
  total: number,
  deposit: number,
  canChangeDeposit = false,
): number => {
  const monthlies = rates.map((rate) =>
    getMinimumMonthlyForRate(rate, total, deposit, canChangeDeposit),
  );
  return Math.min(...monthlies);
};

export const getJobMinimumMonthlyPayment = (
  job?: Job,
  allRates?: GroupedByAprAndDuration,
): number => {
  if (!job || !allRates) return 0;
  const total = job.total?.amount_vat_inclusive?.amount ?? 0;
  const deposit = job.deposit_value.amount;
  const depositType = job.deposit_type;
  const rates = Object.keys(allRates).reduce(
    (ratesArray: FinanceRate[], groupedKey) => {
      const aprDurations = allRates[groupedKey];
      const aprRates = Object.keys(aprDurations).reduce(
        (aprRatesArray: FinanceRate[], key) => {
          const durationRates = aprDurations[key];
          return [...aprRatesArray, ...durationRates];
        },
        [],
      );
      return [...ratesArray, ...aprRates];
    },
    [],
  );
  return getMinimumMonthlyForRates(
    rates,
    total,
    deposit,
    depositType === 'no_deposit',
  );
};
